import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { DiscountSettings } from "@redotech/redo-model/discount/discount-db-parser";
import { TrackingPageDiscountSource } from "@redotech/redo-model/generated-discount-code-definition";
import { Provider } from "@redotech/redo-model/order";
import { orderTrackingOrderSources } from "@redotech/redo-model/order-tracking/order-tracking-types";
import { TrackingPageProductUpsellBlock } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { isBlacklistedProductName } from "@redotech/redo-model/product-blacklist";
import { RedoRecommendedProduct } from "@redotech/redo-model/redo-recommended-product-schema";
import { isDef } from "@redotech/util/checks";
import classNames from "classnames";
import { memo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { Text } from "../../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../../context/tracking-page-context";
import { getHeaderStyles, getLinkStyles } from "../shared/block-styles";
import { BlockWrapper } from "../shared/block-wrapper";
import {
  getInitialExpirationDuration,
  getUpsellText,
} from "./discount-handling";
import { ExpirationCountdown } from "./expiration-countdown";
import * as productUpsellCss from "./product-upsell.module.css";
import { RecommendedProductComponent } from "./recommended-product";
import { createUpsellRedirectHandler } from "./upsell-redirect";

export const ProductUpsell = memo(function ProductUpsell({
  _id,
  recommendedProductFilterId,
  upsellText,
  imageAspectRatio,
  discountId,
  shopFullUrl,
  checkoutButtonSettings,
  dropdownSettings,
  provider: blockProvider,
  redirectToProductPage,
}: TrackingPageProductUpsellBlock) {
  const context = useRequiredContext(TrackingPageContext);
  const {
    order,
    returnAppSettings,
    environment,
    productUpsell,
    pageSettings,
    generateDiscountCode,
  } = context;

  const rehydrationCounter =
    (context.environment === TrackingPageEnvironment.BUILDER
      ? context.blockRehydrationCounters?.[_id]
      : undefined) ?? 0;

  const recommendedProductsLoad = useLoad(async (): Promise<
    RedoRecommendedProduct[]
  > => {
    if (!order?.source) {
      return [];
    }

    const allProductIds = order.lineItems
      .map((lineItem) => lineItem.productId)
      .filter(isDef);

    // Prefer line items that aren't package protection, gift cards, store
    // credit, etc. — those make for lousy "related product" seeds. Keep the
    // blacklisted items only if they're the only thing in the order so we
    // still have something to seed recommendations with.
    const nonBlacklisted = order.lineItems.filter(
      (lineItem) =>
        !isBlacklistedProductName(lineItem.name, lineItem.variantTitle),
    );
    const seedLineItems =
      nonBlacklisted.length > 0 ? nonBlacklisted : order.lineItems;
    const seedProductIds = seedLineItems
      .map((lineItem) => lineItem.productId)
      .filter(isDef);

    const resolvedProvider =
      blockProvider &&
      (orderTrackingOrderSources as readonly Provider[]).includes(blockProvider)
        ? (blockProvider as (typeof orderTrackingOrderSources)[number])
        : order.source;

    const recommendedProducts =
      await productUpsell.getRecommendedProductsByProvider({
        provider: resolvedProvider,
        numRecommendations: 3,
        excludedVariantIds: order.lineItems
          .map((lineItem) => lineItem.variantId)
          .filter(isDef),
        excludedParentProductIds: allProductIds,
        customer: { email: order.customer.email },
        recommendedProductFilterId: recommendedProductFilterId ?? undefined,
        productIds: seedProductIds,
      });
    return recommendedProducts.products;
  }, [order, recommendedProductFilterId, blockProvider, rehydrationCounter]);

  // Fetch discount and generate code if discountId is provided
  const directDiscountLoad = useLoad<
    { discountCode?: string; discountSettings?: DiscountSettings } | undefined
  >(async () => {
    if (!order || !discountId || order.source !== Provider.SHOPIFY) {
      return undefined;
    }

    const { discountCode, discountSettings } = await generateDiscountCode(
      discountId,
      TrackingPageDiscountSource.UPSELL,
      order.internalMongoId,
    );

    return { discountCode, discountSettings };
  }, [order?.internalMongoId, discountId]);

  const displayUpsellText = getUpsellText(
    upsellText,
    directDiscountLoad.value?.discountSettings,
  );
  const initialMilliseconds = getInitialExpirationDuration(
    directDiscountLoad.value?.discountSettings,
  );

  const storeURL = `https://${returnAppSettings.storeUrl}`;
  const headerStyles = getHeaderStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const activeDiscountCode = directDiscountLoad.value?.discountCode;

  const isLoading =
    recommendedProductsLoad.pending || directDiscountLoad.pending || !order;

  return (
    <BlockWrapper
      className={classNames(productUpsellCss.upsellProductsCard, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [productUpsellCss.editMode]:
          environment === TrackingPageEnvironment.BUILDER,
      })}
      environment={environment}
      loading={isLoading}
      pageSettings={pageSettings}
      withBorder
      withScrollbar
    >
      {order && (
        <Flex dir="column" height="full" p="3xl">
          <Flex
            dir="column"
            inert={environment === TrackingPageEnvironment.BUILDER}
          >
            <Flex
              align="center"
              className={productUpsellCss.headerRow}
              flexDirection="row"
              justify="space-between"
            >
              <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
                {displayUpsellText}
              </Text>
              <ExternalLink
                onClick={async () => {
                  if (
                    environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
                    !shopFullUrl
                  ) {
                    const upsellRedirect = createUpsellRedirectHandler({
                      order,
                      selectWindow: productUpsell.selectWindow,
                      returnAppSettings,
                      logPageClick: productUpsell.logPageClick,
                      redirectToWebsiteWithDiscountCode:
                        productUpsell.redirectToWebsiteWithDiscountCode,
                      discountCode: activeDiscountCode,
                    });
                    upsellRedirect.logUpsellAndRedirect();
                  }
                }}
                style={linkStyles}
                url={shopFullUrl}
              >
                Shop full store
              </ExternalLink>
            </Flex>
            {initialMilliseconds && (
              <ExpirationCountdown initialMilliseconds={initialMilliseconds} />
            )}
          </Flex>

          <Flex
            dir="column"
            flex={1}
            inert={environment === TrackingPageEnvironment.BUILDER}
            justify="space-evenly"
          >
            {recommendedProductsLoad.value ? (
              <IterableMap
                items={recommendedProductsLoad.value}
                keyFn={(item: RedoRecommendedProduct) => item.id}
              >
                {(product, index) => (
                  <RecommendedProductComponent
                    checkoutButtonSettings={checkoutButtonSettings}
                    discountSettings={
                      directDiscountLoad.value?.discountSettings
                    }
                    dropdownSettings={dropdownSettings}
                    imageAspectRatio={imageAspectRatio}
                    index={index}
                    product={product}
                    upsellRedirect={
                      environment === TrackingPageEnvironment.CUSTOMER_PORTAL
                        ? createUpsellRedirectHandler({
                            order,
                            selectWindow: productUpsell.selectWindow,
                            returnAppSettings,
                            logPageClick: productUpsell.logPageClick,
                            redirectToWebsiteWithDiscountCode:
                              productUpsell.redirectToWebsiteWithDiscountCode,
                            discountCode: activeDiscountCode,
                            redirectUrl: `${storeURL}/products/${product.handle}`,
                            redirectToProductPage,
                          })
                        : undefined
                    }
                  />
                )}
              </IterableMap>
            ) : null}
          </Flex>
        </Flex>
      )}
    </BlockWrapper>
  );
});
